import React, { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { uploadFile } from "@/lib/files";
import { Upload, Loader2, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { toast } from "sonner";

const TRENDS = ["up", "flat", "down"];
export function TrendIcon({ trend }) {
  if (trend === "up") return <ArrowUp strokeWidth={2} className="w-5 h-5 text-green-500" />;
  if (trend === "down") return <ArrowDown strokeWidth={2} className="w-5 h-5 text-red-500" />;
  return <Minus strokeWidth={2} className="w-5 h-5 text-foreground/60" />;
}

export function TrendControl({ value, onChange, testId }) {
  const v = value || "flat";
  const next = () => onChange(TRENDS[(TRENDS.indexOf(v) + 1) % TRENDS.length]);
  return (
    <button type="button" onClick={next} data-testid={testId}
      className="inline-flex items-center justify-center w-9 h-9 rounded-sm border border-gold/40 hover:border-gold transition-colors">
      <TrendIcon trend={v} />
    </button>
  );
}

export function EditableText({ label, value, onChange, editable, placeholder, testId, mono }) {
  return (
    <div className="space-y-1.5">
      {label && <div className="ht-uppercase-label">{label}</div>}
      {editable ? (
        <Input
          data-testid={testId}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-sm bg-background h-9"
        />
      ) : (
        <div data-testid={testId} className={`text-base ${mono ? "tabular-nums" : ""}`}>
          {value || <span className="text-muted-foreground">—</span>}
        </div>
      )}
    </div>
  );
}

export function EditableNumber({ label, value, onChange, editable, testId, min = 0, max }) {
  return (
    <div className="space-y-1.5">
      {label && <div className="ht-uppercase-label">{label}</div>}
      {editable ? (
        <Input
          data-testid={testId}
          type="number"
          value={value ?? 0}
          min={min}
          max={max}
          onChange={(e) => onChange(Number(e.target.value))}
          className="rounded-sm bg-background h-9 tabular-nums"
        />
      ) : (
        <div data-testid={testId} className="text-base tabular-nums">{value ?? 0}</div>
      )}
    </div>
  );
}

export function EditableTextarea({ label, value, onChange, editable, rows = 4, placeholder, testId }) {
  return (
    <div className="space-y-1.5">
      {label && <div className="ht-uppercase-label">{label}</div>}
      {editable ? (
        <Textarea
          data-testid={testId}
          rows={rows}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-sm bg-background"
        />
      ) : (
        <p data-testid={testId} className="text-base leading-relaxed whitespace-pre-wrap">
          {value || <span className="text-muted-foreground">—</span>}
        </p>
      )}
    </div>
  );
}

export function UploadButton({ onUploaded, accept, label = "Upload", testId, variant = "outline", size = "sm" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await uploadFile(file);
      onUploaded(res);
      toast.success("Uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={handle} data-testid={testId ? `${testId}-input` : undefined} />
      <Button type="button" variant={variant} size={size} disabled={busy}
        onClick={() => ref.current?.click()} className="rounded-sm" data-testid={testId}>
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload strokeWidth={1.5} className="w-4 h-4 mr-2" />}
        {label}
      </Button>
    </>
  );
}
